<template>
    <div class="date-time-picker__wrapper">
        <label class="date-time-picker__label" :for="name">
            {{ label }}
        </label>
        <div :class="datePickerClasses">
            <div
                v-on-click-outside="onDatePickerClickOutside"
                class="date-time-picker__date-picker"
                :class="`date-time-picker__date-picker--${calendarPosition}`"
                data-testid="date-time-picker"
                role="group"
                :aria-label="`Select ${label}`"
            >
                <NuxtIcon name="ri:calendar-todo-line" size="16" class="date-time-picker__icon" />
                <input
                    :id="name"
                    readonly
                    class="date-time-picker__value"
                    :value="formattedDate"
                    :placeholder="$t('searchWidget.date')"
                    ref="inputRef"
                    @click="dateDropdownOpened ? closeDatePicker() : openDatePicker()"
                    @keydown.enter.prevent="dateDropdownOpened ? closeDatePicker() : openDatePicker()"
                />

                <Backdrop v-show="dateDropdownOpened" v-model="hasBackdrop">
                    <client-only>
                        <DatePicker
                            v-if="dateDropdownOpened || formActive"
                            :key="datePickerKey"
                            :value="date"
                            :min-date="minDate"
                            :max-date="maxDate"
                            :label="label"
                            :label-mobile="labelMobile"
                            :name="name"
                            @update:value="onDateChange"
                            @close="closeCalendar"
                            @toggle-calendar="closeCalendar"
                    /></client-only>
                </Backdrop>
            </div>
        </div>
        <ErrorMessage v-if="displayError" :message="errorMessage" class="date-time-picker__error-message" />
    </div>
</template>

<script setup lang="ts">
import { vOnClickOutside } from '@vueuse/components';

import { useIsMobileView } from '@nuxt-monorepo/ui/composables/isMobileView';
import screens from '#tailwind-config/theme/screens';
const { isMobileView } = useIsMobileView(screens.md);

import { add, formatWithLocale, parseISO, DATE_TIME_FORMATS } from '../../date-utilities';
import Backdrop from '../Common/Backdrop.vue';
import ErrorMessage from '../Common/ErrorMessage.vue';
const DatePicker = defineAsyncComponent({ loader: async () => await import('./DatePicker.vue') });

const props = defineProps({
    date: {
        type: String,
        default: '',
    },

    minDate: {
        type: [String, Date],
        default: () => new Date(),
    },

    maxDate: {
        type: [String, Date],
        default: () => add(new Date(), { years: 1 }),
    },

    error: {
        type: Boolean,
        default: false,
    },

    errorMessage: {
        type: [String, Array],
        default: '',
    },

    dateFormat: {
        type: String,
        default: DATE_TIME_FORMATS.month_day_year,
    },

    label: {
        type: String,
        default: '',
    },

    labelMobile: {
        type: String,
        default: '',
    },

    calendarPosition: {
        type: String,
        default: 'left',
    },

    name: {
        type: String,
        default: '',
    },

    dropdownOpened: {
        type: Boolean,
        default: false,
    },

    formActive: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['focus', 'date-change', 'blur', 'update:dropdownOpened']);

const datePickerKey = ref(1);

const dateDropdownOpened = computed({
    get(): boolean {
        return props.dropdownOpened;
    },
    set(val: boolean) {
        emit('update:dropdownOpened', val);
    },
});

const displayError = computed(() => {
    return props.error && !dateDropdownOpened.value;
});

const hasBackdrop = computed({
    get(): boolean {
        return dateDropdownOpened.value && isMobileView.value;
    },
    set(): void {
        closeDatePicker();
    },
});

const { locale, locales } = useI18n();
const localeObject = locales.value.filter((e) => e.code === locale.value)?.[0];

const formattedDate = computed(() => {
    return props.date
        ? formatWithLocale(parseISO(props.date), props.dateFormat, localeObject?.language || 'en-GB')
        : '';
});

const datePickerClasses = computed(() => [
    'date-time-picker',
    { 'date-time-picker--error': props.error },
    {
        'date-time-picker--active': dateDropdownOpened.value,
    },
]);

const onDateChange = (date: string) => {
    closeDatePicker();
    emit('date-change', date);
};

const openDatePicker = () => {
    dateDropdownOpened.value = true;
    emit('focus');
};

const closeDatePicker = () => {
    dateDropdownOpened.value = false;
    emit('blur');
};

const onDatePickerClickOutside = () => {
    if (dateDropdownOpened.value) {
        dateDropdownOpened.value = false;
        return;
    }
    if (dateDropdownOpened.value) {
        closeDatePicker();
    }
};
const closeCalendar = () => {
    setTimeout(() => {
        closeDatePicker();
        inputRef.value.blur();
    }, 200);
};

watch(
    () => props.minDate,
    () => (datePickerKey.value += 1)
);

// Keyboard navigation
const inputRef = ref();
const focusInput = () => {
    inputRef?.value?.focus();
};
defineExpose({ focusInput });
</script>

<style lang="postcss" scoped>
.date-time-picker__wrapper {
    @apply relative w-full;
}

.date-time-picker {
    @apply relative flex rounded-lg bg-white h-10 border border-wl-base-soft-gray cursor-pointer transition-colors duration-300;

    @screen lg {
        @apply h-10;
    }

    &.date-time-picker--active {
        @apply outline outline-4 outline-wl-brand-pr border-transparent -outline-offset-2;
    }

    &.date-time-picker--error {
        @apply border-red-500;
        &.date-time-picker--active {
            @apply border-transparent;
        }
    }

    &__label {
        @apply block mb-2 font-semibold text-wl-base-dark-grey;
    }

    &__icon {
        @apply text-wl-base-dark-grey absolute left-3 pointer-events-none;
    }
}

.date-time-picker__date-picker,
.date-time-picker__time-picker {
    @apply relative flex items-center flex-grow;

    @screen lg {
        @apply pr-0;
    }

    :deep(.vc-pane-container) {
        .vc-title-wrapper {
            button.vc-title {
                @apply pointer-events-none;
            }
        }
    }
}

.date-time-picker__date-picker {
    @apply w-7/12;
}

.date-time-picker__time-picker {
    @apply w-5/12 pl-0;
}

.date-time-picker__value {
    @apply text-base w-full cursor-pointer bg-transparent placeholder:text-wl-base-dark-grey pl-9 pr-3 py-[7px];
    &:focus-visible {
        @apply outline outline-wl-brand-pr border-transparent rounded-lg;
        outline-offset: -1px;
        outline-width: 4px;
    }
}

.date-time-picker:deep(.time-picker) {
    @screen lg {
        top: -150px;
        left: 0;
    }
}

.date-time-picker__error-message {
    @apply absolute right-0;

    top: calc(100% + 0.5rem);
    width: auto !important;

    @screen lg {
        @apply left-0 right-auto;
    }
}

.date-time-picker__date-picker {
    &--left {
        :deep(.calendar-container) {
            @apply left-0;
        }
    }
    &--right {
        :deep(.calendar-container) {
            @apply right-0;
        }
    }
    &--center {
        :deep(.calendar-container) {
            @apply -translate-x-1/2 left-0;
            @media (max-width: theme('screens.lg')) {
                @apply translate-x-0;
            }
        }
    }
}
</style>
